import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarClock, Phone, PhoneCall } from "lucide-react";
import { CalendarFeedCard } from "@/components/CalendarFeedCard";
import { RedialBar, AddAppointment, NoShowToggle, RedialReport } from "@/components/Redial";

const when = (a) => (a.appointment_at ? new Date(a.appointment_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : a.appointment || "Time not set");

const TYPE = { phone: "Phone", office: "In person - office", in_home: "In-home visit" };
const NoShow = ({ a }) => {
  if (a.noshow_call?.status) return <p className="mt-1 text-xs text-slate-500" data-testid={`appt-noshow-result-${a.id}`}><b>Missed-meeting follow-up:</b> {a.noshow_call.status === "calling" ? "Ava is calling the lead now…" : a.noshow_call.status === "skipped" ? "Not called (no phone or the lead asked not to be called)." : a.noshow_call.result}</p>;
  if (a.no_show_at) return <p className="mt-1 text-xs text-slate-500" data-testid={`appt-noshow-pending-${a.id}`}>Marked as missed - Ava will call the lead 15 minutes after the start time.</p>;
  return null;
};

const AvaCall = ({ a, past, reload }) => {
  if (a.appointment_type === "office" || a.appointment_type === "in_home") return null;
  if (a.appt_call?.status) return <p className="mt-1 text-xs text-slate-500" data-testid={`appt-ava-call-result-${a.id}`}><b>Ava's appointment call:</b> {a.appt_call.status === "calling" ? "Ava is calling the lead now…" : a.appt_call.result}</p>;
  if (past || !a.appointment_at) return null;
  const on = a.auto_call !== false;
  const toggle = async () => {
    try { await api.post(`/appointments/${a.id}/auto-call`, { on: !on }); toast.success(on ? "Ava won't call at the appointment time" : "Ava will call the lead at the appointment time"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Button size="sm" variant={on ? "default" : "outline"} className={`mt-2 h-auto whitespace-normal py-1.5 text-left ${on ? "bg-teal-700 hover:bg-teal-800" : ""}`} onClick={toggle} data-testid={`appt-ava-call-btn-${a.id}`}
      title="At the appointment time Ava calls the lead, confirms they're available, and transfers them to the loan officer (or the broker if the loan officer doesn't answer)">
      <PhoneCall className="mr-1.5 h-4 w-4" />{on ? "Ava calls at appointment time · tap to cancel" : "Have Ava call at appointment time"}
    </Button>
  );
};

const Row = ({ a, admin, reload }) => {
  const past = a.appointment_at && new Date(a.appointment_at) < new Date();
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 p-4" data-testid={`appt-row-${a.id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900">{a.prospect_name || "Unknown lead"} <Badge variant="outline" className="ml-1 border-indigo-200 text-indigo-700" data-testid={`appt-type-${a.id}`}>{TYPE[a.appointment_type] || "Phone"}</Badge></div>
        {a.appointment_type === "in_home" && <div className="text-xs font-medium text-slate-700" data-testid={`appt-location-${a.id}`}>Address: {a.appointment_location || a.address || "Ask the lead for the address"}</div>}
        {a.appointment_type === "office" && <div className="text-xs font-medium text-slate-700" data-testid={`appt-location-${a.id}`}>Office: {a.appointment_location || a.office_address || "235 North Westmonte Drive, Office 225, Altamonte Springs, FL 32714"}</div>}
        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-500">
          {a.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{a.phone}</span>}
          {a.email && <span>{a.email}</span>}{a.address && <span>{a.address}</span>}
        </div>
        {a.summary && <p className="mt-1 line-clamp-2 text-sm text-slate-600">{a.summary}</p>}
        {a.recording_url && <a href={a.recording_url} target="_blank" rel="noreferrer" className="text-xs text-amber-700 underline" data-testid={`appt-recording-${a.id}`}>Call recording</a>}
        {a.reminder_call?.status && <p className="mt-1 text-xs text-slate-500" data-testid={`appt-reminder-${a.id}`}><b>Reminder call:</b> {a.reminder_call.status === "calling" ? "Ava is calling the lead now…" : a.reminder_call.result}</p>}
        <NoShow a={a} past={past} reload={reload} />
        {past && a.appointment_status !== "cancelled" && <NoShowToggle a={a} reload={reload} />}
        <AvaCall a={a} past={past} reload={reload} />
      </div>
      <div className="text-right">
        <div className="flex items-center justify-end gap-1 text-sm font-medium text-slate-900" data-testid={`appt-time-${a.id}`}><CalendarClock className="h-4 w-4 text-amber-600" />{when(a)}</div>
        {a.appointment_at && a.appointment && <div className="text-xs text-slate-400">"{a.appointment}"</div>}
        {admin && <div className="mt-1 text-xs text-slate-500" data-testid={`appt-mlo-${a.id}`}>MLO: {a.mlo_name || "—"}</div>}
        <Badge variant="outline" className={`mt-1 ${past ? "text-slate-400" : "border-emerald-200 text-emerald-700"}`}>{past ? "Past" : "Upcoming"}</Badge>
        {a.appointment_status && <Badge variant="outline" className={`ml-1 mt-1 capitalize ${a.appointment_status === "cancelled" ? "border-red-200 text-red-700" : "border-amber-200 text-amber-700"}`} data-testid={`appt-status-${a.id}`}>{a.appointment_status}</Badge>}
      </div>
    </div>
  );
};

export default function Appointments() {
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const [rows, setRows] = useState(null);
  const [mlo, setMlo] = useState("");
  const [show, setShow] = useState("upcoming");
  const [auto, setAuto] = useState(false);
  const load = useCallback(() => api.get("/appointments").then((r) => { setRows(r.data.appointments); setAuto(r.data.redial_autopilot); }).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const mlos = [...new Map((rows || []).map((a) => [a.mlo_id, a.mlo_name])).entries()];
  const now = new Date();
  const list = (rows || []).filter((a) => (!mlo || a.mlo_id === mlo) &&
    (show === "all" || (show === "upcoming" ? !a.appointment_at || new Date(a.appointment_at) >= now : a.appointment_at && new Date(a.appointment_at) < now)));
  if (show === "past") list.reverse();
  return (
    <div data-testid="appointments-page">
      <PageHeader title={admin ? "Appointments - All MLOs" : "Appointments"} subtitle="Phone, office and in-home appointments Ava booked with leads." />
      <CalendarFeedCard />
      <RedialBar autopilot={auto} admin={admin} reload={load} mlo={mlo} count={(rows || []).filter((a) => a.appointment_status === "no-show" && !a.redial?.stopped && (!mlo || a.mlo_id === mlo)).length} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <AddAppointment admin={admin} reload={load} />
        {["upcoming", "past", "all"].map((k) => (
          <button key={k} onClick={() => setShow(k)} data-testid={`appt-filter-${k}`}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium capitalize transition-colors ${show === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{k}</button>))}
        {admin && <select value={mlo} onChange={(e) => setMlo(e.target.value)} className="h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="appt-mlo-filter">
          <option value="">All MLOs</option>{mlos.map(([id, n]) => <option key={id} value={id}>{n || id}</option>)}
        </select>}
      </div>
      <Card className="divide-y divide-slate-100" data-testid="appointments-list">
        {rows === null ? <p className="p-8 text-center text-sm text-slate-400">Loading…</p>
          : list.length === 0 ? <div className="flex flex-col items-center gap-2 p-12 text-center text-slate-400"><CalendarClock className="h-9 w-9" /><p className="text-sm">No {show === "all" ? "" : show} appointments.</p></div>
          : list.map((a) => <Row key={a.id} a={a} admin={admin} reload={load} />)}
      </Card>
      <RedialReport admin={admin} mlo={mlo} />
    </div>
  );
}
